import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { settingsAPI, uploadAPI } from '../lib/api';
import {
  LayoutDashboard,
  Users,
  GraduationCap,
  MapPin,
  Calendar,
  CalendarDays,
  FileText,
  Activity,
  Settings,
  Bell,
  LogOut,
  Menu,
  X,
  ChevronRight,
  BookOpen,
  Info,
  HelpCircle,
  Camera,
  Loader2,
  School,
  ClipboardList,
  UserCog,
  MessageSquare,
  Globe,
  User,
  Shield,
  Handshake,
  Search,
  Zap,
  BarChart3,
  Gift,
} from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/students', label: 'Students', icon: Users },
  { path: '/coaches', label: 'Coaches', icon: GraduationCap },
  { path: '/schools', label: 'Schools', icon: School },
  { path: '/school-registrations', label: 'School Registrations', icon: ClipboardList },
  { path: '/manage/programs', label: 'Programs', icon: BookOpen },
  { path: '/locations', label: 'Locations', icon: MapPin },
  { path: '/lessons', label: 'Lessons', icon: Calendar },
  { path: '/calendar', label: 'Calendar', icon: CalendarDays },
  { path: '/admin/lesson-requests', label: 'Lesson Requests', icon: MessageSquare, adminOnly: true },
  { path: '/reports', label: 'Reports', icon: FileText },
  { path: '/activity', label: 'Activity', icon: Activity },
  { path: '/about', label: 'About', icon: Info },
  { path: '/manual', label: 'User Manual', icon: HelpCircle },
];

export const Layout = ({ children }) => {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications] = useState([]);
  const [settings, setSettings] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await settingsAPI.get();
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to load settings');
    }
  };

  const getLogoUrl = () => {
    if (!settings?.logo_url) return null;
    if (settings.logo_url.startsWith('http')) return settings.logo_url;
    return `${BACKEND_URL}${settings.logo_url}`;
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please select a valid image file');
      return;
    }

    setUploadingLogo(true);
    try {
      const response = await uploadAPI.uploadPhoto(file, 'logo', settings?.id || 'academy');
      // Update settings with new logo
      await settingsAPI.update({ ...settings, logo_url: response.data.photo_url });
      setSettings(prev => ({ ...prev, logo_url: response.data.photo_url }));
      toast.success('Logo updated successfully');
    } catch (error) {
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  };

  const logoUrl = getLogoUrl();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 z-50 h-screen w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-slate-100">
            <input
              ref={logoInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={handleLogoUpload}
              className="hidden"
              data-testid="sidebar-logo-input"
            />
            <div className="flex items-center gap-3 group">
              <div 
                className={`relative w-10 h-10 rounded-xl overflow-hidden flex items-center justify-center shadow-md transition-transform ${isAdmin() ? 'cursor-pointer group-hover:scale-105' : ''}`}
                onClick={() => isAdmin() && logoInputRef.current?.click()}
                data-testid="sidebar-logo"
              >
                {uploadingLogo ? (
                  <div className="w-full h-full bg-primary/10 flex items-center justify-center">
                    <Loader2 className="w-5 h-5 animate-spin text-primary" />
                  </div>
                ) : logoUrl ? (
                  <img 
                    src={logoUrl} 
                    alt="Academy Logo" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-primary to-primary-900 flex items-center justify-center text-white font-bold text-lg">
                    ES
                  </div>
                )}
                {isAdmin() && !uploadingLogo && (
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Camera className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>
              <Link to="/dashboard" data-testid="logo-link">
                <h1 className="font-heading font-bold text-slate-900 text-sm hover:text-primary transition-colors">
                  {settings?.academy_name || 'Eswatini Seals'}
                </h1>
                <p className="text-xs text-slate-500">Swim Academy</p>
              </Link>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-4">
            <ul className="space-y-1 px-3">
              {navItems
                .filter(item => !item.adminOnly || isAdmin())
                .map((item) => {
                const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
                const Icon = item.icon;
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      data-testid={`nav-${item.path.replace('/', '')}`}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                        isActive 
                          ? 'bg-primary/10 text-primary' 
                          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                      }`}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <Icon className="w-5 h-5" />
                      <span>{item.label}</span>
                      {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Settings link for admin */}
          {isAdmin() && (
            <div className="px-3 pb-2 space-y-1">
              {/* Website Group */}
              <div className="pt-2 pb-1">
                <p className="px-3 text-xs font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-2">
                  <Globe className="w-3.5 h-3.5" />
                  Website
                </p>
              </div>
              <Link
                to="/admin/founder"
                data-testid="nav-founder"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  location.pathname === '/admin/founder'
                    ? 'bg-primary/10 text-primary'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
              >
                <User className="w-5 h-5" />
                <span>Founder</span>
              </Link>
              <Link
                to="/admin/social-proof"
                data-testid="nav-social-proof"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  location.pathname === '/admin/social-proof'
                    ? 'bg-primary/10 text-primary'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
              >
                <MessageSquare className="w-5 h-5" />
                <span>Social Proof</span>
              </Link>
              <Link
                to="/admin/safety-standards"
                data-testid="nav-safety"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  location.pathname === '/admin/safety-standards'
                    ? 'bg-primary/10 text-primary'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
              >
                <Shield className="w-5 h-5" />
                <span>Safety & Standards</span>
              </Link>
              <Link
                to="/admin/programs-pathways"
                data-testid="nav-programs"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  location.pathname === '/admin/programs-pathways'
                    ? 'bg-primary/10 text-primary'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
              >
                <GraduationCap className="w-5 h-5" />
                <span>Programs</span>
              </Link>
              <Link
                to="/admin/partnerships"
                data-testid="nav-partnerships"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  location.pathname === '/admin/partnerships'
                    ? 'bg-primary/10 text-primary'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
              >
                <Handshake className="w-5 h-5" />
                <span>Partnerships</span>
              </Link>
              <Link
                to="/admin/global-presets"
                data-testid="nav-global-presets"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  location.pathname === '/admin/global-presets'
                    ? 'bg-primary/10 text-primary'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
              >
                <Globe className="w-5 h-5" />
                <span>Global Presets</span>
              </Link>
              <Link
                to="/admin/seo"
                data-testid="nav-seo"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  location.pathname === '/admin/seo'
                    ? 'bg-primary/10 text-primary'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
              >
                <Search className="w-5 h-5" />
                <span>SEO</span>
              </Link>
              <Link
                to="/admin/conversion"
                data-testid="nav-conversion"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  location.pathname === '/admin/conversion'
                    ? 'bg-primary/10 text-primary'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
              >
                <Zap className="w-5 h-5" />
                <span>Conversion</span>
              </Link>
              <Link
                to="/admin/analytics-simple"
                data-testid="nav-analytics"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  location.pathname === '/admin/analytics-simple'
                    ? 'bg-primary/10 text-primary'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
              >
                <BarChart3 className="w-5 h-5" />
                <span>Analytics</span>
              </Link>
              <Link
                to="/admin/promo"
                data-testid="nav-promo"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  location.pathname === '/admin/promo'
                    ? 'bg-primary/10 text-primary'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
              >
                <Gift className="w-5 h-5" />
                <span>Promo</span>
              </Link>
              
              {/* Admin Group */}
              <div className="pt-3 pb-1">
                <p className="px-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Admin</p>
              </div>
              <Link
                to="/users"
                data-testid="nav-users"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  location.pathname === '/users'
                    ? 'bg-primary/10 text-primary'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
              >
                <UserCog className="w-5 h-5" />
                <span>User Management</span>
              </Link>
              <Link
                to="/settings"
                data-testid="nav-settings"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  location.pathname === '/settings'
                    ? 'bg-primary/10 text-primary'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
              >
                <Settings className="w-5 h-5" />
                <span>Settings</span>
              </Link>
            </div>
          )}

          {/* User section */}
          <div className="p-4 border-t border-slate-100">
            <div className="flex items-center gap-3">
              <Avatar className="h-9 w-9">
                <AvatarFallback className="bg-primary/10 text-primary text-sm font-semibold">
                  {getInitials(user?.name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
                <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                className="text-slate-400 hover:text-slate-600"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:ml-64">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-slate-100">
          <div className="flex items-center justify-between px-4 lg:px-6 h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setSidebarOpen(true)}
                data-testid="mobile-menu-btn"
              >
                <Menu className="w-5 h-5" />
              </Button>
              <h2 className="font-heading font-semibold text-slate-900 text-lg hidden sm:block">
                {navItems.find(item => location.pathname.startsWith(item.path))?.label || 'Dashboard'}
              </h2>
            </div>

            <div className="flex items-center gap-3">
              {/* Notifications */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                    <Bell className="w-5 h-5 text-slate-600" />
                    {notifications.length > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-accent text-white text-xs font-bold rounded-full flex items-center justify-center">
                        {notifications.length}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80">
                  <div className="p-3 border-b">
                    <h3 className="font-semibold text-sm">Notifications</h3>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="p-4 text-center text-sm text-slate-500">
                        No new notifications
                      </div>
                    ) : (
                      notifications.map((notif, i) => (
                        <DropdownMenuItem key={i} className="p-3">
                          <div>
                            <p className="text-sm font-medium">{notif.title}</p>
                            <p className="text-xs text-slate-500">{notif.message}</p>
                          </div>
                        </DropdownMenuItem>
                      ))
                    )}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* User menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2" data-testid="user-menu-btn">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
                        {getInitials(user?.name)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden sm:inline text-sm font-medium">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <div className="p-2">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-slate-500">{user?.email}</p>
                    <Badge variant="secondary" className="mt-1 text-xs capitalize">
                      {user?.role}
                    </Badge>
                  </div>
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
